import { useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import "./confirmDialog.css";

/**
 * Modal elegante para confirmação (substitui window.confirm / window.prompt).
 *
 * Props:
 *   - title: título do diálogo
 *   - message: mensagem principal
 *   - withInput: se true, mostra um campo de input (substitui prompt)
 *   - inputPlaceholder, inputDefaultValue
 *   - confirmText, cancelText
 *   - variant: 'default' | 'danger' | 'success'
 *   - onConfirm(value): callback - value é boolean ou string (se withInput)
 *   - onCancel(): callback
 */
export default function ConfirmDialog({
  title,
  message,
  withInput,
  inputPlaceholder,
  inputDefaultValue,
  confirmText,
  cancelText,
  variant,
  onConfirm,
  onCancel,
}) {
  const [inputValue, setInputValue] = useState(inputDefaultValue || "");

  const handleClickConfirm = () => {
    if (withInput) {
      if (!inputValue.trim()) return;
      onConfirm(inputValue.trim());
    } else {
      onConfirm(true);
    }
  };

  return createPortal(
    <div className="confirm-overlay" onClick={onCancel}>
      <div
        className={`confirm-card confirm-${variant}`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="confirm-header">
            <div className="confirm-icon-box">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="confirm-title">{title}</h3>
          </div>
        )}

        {message && <p className="confirm-message">{message}</p>}

        {withInput && (
          <textarea
            className="confirm-input"
            placeholder={inputPlaceholder || "Digite aqui..."}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
            rows={3}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleClickConfirm();
              }
            }}
          />
        )}

        <div className="confirm-actions">
          <button
            type="button"
            className="confirm-btn confirm-btn-cancel"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`confirm-btn confirm-btn-confirm confirm-btn-${variant}`}
            onClick={handleClickConfirm}
            disabled={withInput && !inputValue.trim()}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

ConfirmDialog.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  withInput: PropTypes.bool,
  inputPlaceholder: PropTypes.string,
  inputDefaultValue: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  variant: PropTypes.oneOf(["default", "danger", "success"]),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ConfirmDialog.defaultProps = {
  title: "",
  message: "",
  withInput: false,
  inputPlaceholder: "",
  inputDefaultValue: "",
  confirmText: "Confirmar",
  cancelText: "Cancelar",
  variant: "default",
};
